import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icons } from './Icons';

export type Option = { value: string; label: string; description?: string; hint?: string };

// A fully custom, searchable, keyboard-navigable select. Never a native <select>.
// Renders as a popover on desktop and a bottom sheet on mobile. Includes a filter
// for long lists.
export function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchable = false,
  searchPlaceholder = 'Search…',
  ariaLabel,
  buttonClassName = '',
  renderButton,
}: {
  options: Option[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  ariaLabel?: string;
  buttonClassName?: string;
  renderButton?: (selected: Option | null) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = query
    ? options.filter((o) => (o.label + ' ' + (o.description ?? '') + ' ' + (o.hint ?? '')).toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (open && searchable) searchRef.current?.focus();
    if (open) setActive(Math.max(0, filtered.findIndex((o) => o.value === value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[active]) commit(filtered[active].value);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className={
          buttonClassName ||
          `flex w-full cursor-pointer items-center justify-between gap-2 rounded-2xl border bg-white px-4 py-3.5 text-left font-body transition-colors ${
            open ? 'border-terracotta shadow-glow' : 'border-stone hover:border-terracotta/50'
          }`
        }
      >
        {renderButton ? (
          renderButton(selected)
        ) : (
          <span className={selected ? 'text-plum' : 'text-plum/45'}>{selected ? selected.label : placeholder}</span>
        )}
        {!renderButton && <Icons.chevronDown width={18} height={18} className={`shrink-0 text-plum/40 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Anchored dropdown. Absolute (not fixed) so it works correctly inside a
                transformed modal on every viewport. */}
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              role="listbox"
              onKeyDown={onListKey}
              tabIndex={-1}
              className="absolute left-0 top-[calc(100%+8px)] z-50 w-full min-w-[200px] max-h-[60vh] overflow-hidden rounded-2xl border border-stone bg-white shadow-lift"
            >
              {searchable && (
                <div className="sticky top-0 flex items-center gap-2 border-b border-stone bg-white px-3 py-2.5">
                  <Icons.search width={16} height={16} className="text-plum/40" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActive(0);
                    }}
                    placeholder={searchPlaceholder}
                    className="w-full bg-transparent font-body text-sm text-plum outline-none placeholder:text-plum/40"
                  />
                </div>
              )}
              <div ref={listRef} className="max-h-[46vh] overflow-y-auto overscroll-contain p-1.5 sm:max-h-64">
                {filtered.length === 0 && <p className="px-3 py-6 text-center font-body text-sm text-plum/50">No matches.</p>}
                {filtered.map((o, i) => {
                  const isSel = o.value === value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => commit(o.value)}
                      className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                        i === active ? 'bg-mist' : ''
                      } ${isSel ? 'bg-terracotta/8' : ''}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-body text-[15px] font-medium text-plum">{o.label}</span>
                        {o.description && <span className="block truncate font-body text-xs text-plum/55">{o.description}</span>}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {o.hint && <span className="font-mono text-xs text-plum/45">{o.hint}</span>}
                        {isSel && <Icons.check width={16} height={16} className="text-terracotta" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
